import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import SelectField from '../components/SelectField';
import { useNavigate, Link } from 'react-router-dom';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import {
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaClipboardList,
  FaPhone,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTimesCircle,
  FaUserPlus
} from 'react-icons/fa';
import { list, listCampaignsApi, assignusers } from '../api/api';
import { listAssignableUsers } from '../api/users';
import { getAssignments, setAssignment } from '../common/assignments';
import { payloadFromToken } from '../api/auth';
import { MultiSelect } from '../components/MultiSelect';
import DateTimeInput from '../components/DateTimeInput';
import RefreshButton from '../components/RefreshButton';
import ActionDropdown from '../components/ActionDropdown';
import { Formatter } from '../format';
import LoadingState from '../components/LoadingState';
import { isJudgedOnCompliance, qaScoreOf } from '../common/assessment';
import { devLog } from '../common/devLog';

// Bands a call into the pass/marginal/fail rating the table and the campaign
// stats both read.
//
// A call with no Result is 'unrated', never 'fail'. The previous `!compliance`
// test scored every Result-less row as a failure, which is how in-flight and
// orphaned placeholder rows ended up inflating the campaign failure counts.
// campaignStats counts by exact equality on pass/fail/medium, so 'unrated'
// drops out of those tallies while still counting toward the total.
//
// A call with no QA score to band on - a compliance-only scorecard, whose QA_Score is always 0, or
// no QA_Score at all - is a success when it passed compliance, not a failure scored on that 0.
// A critical compliance failure, on any scorecard. A call with no Result is still in flight and is
// not a failure of anything.
const COMPLIANCE_FAIL = 'compliance-fail';
// Sentinel for the Assigned To filter: calls with no assignedto value.
const UNASSIGNED = '__unassigned__';

const isComplianceFailure = (call) => call.Result?.Compliance === false;

export const withComputedRating = (call) => {
  if (!call.Result) {
    return { ...call, computedRating: 'unrated' };
  }

  const compliance = call.Result.Compliance;
  const score = call.Result.QA_Score;

  let rating;
  if (isJudgedOnCompliance(call)) {
    rating = compliance ? 'pass' : 'fail';
  } else if (!compliance || (score < 40)) {
    rating = 'fail';
  } else if (compliance && (score >= 60)) {
    rating = 'pass';
  } else {
    rating = 'medium';
  }

  return { ...call, computedRating: rating };
};

const CampaignDashboard = ({ setAlert }) => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('All');
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedRating, setSelectedRating] = useState('all');
  const [campaigns, setCampaigns] = useState(['All']);
  const [temps, setTemps] = useState(['All']);
  const [selectedTemps, setSelectedTemps] = useState('All')
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [, setAssignmentsState] = useState(getAssignments());
  const [users, setUsers] = useState([]);
  const [assignedFiles, setAssignedFiles] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [modalVis, setModalVis] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const selectAllRef = useRef(null)
  const [startKey, setStartKey] = useState('')
  const [tsmpFrom, setTsmpFrom] = useState('')
  const [campStartKey, setCampStartKey] = useState('')
  const [campAssigned, setcampAssigned] = useState('')
  const [allLoad, setAllLoad] = useState(true)
  const [campLoad, setCampLoad] = useState(true)
  const [jobs, setJobs] = useState(['All'])
  const [selectedJob, setSelectedJob] = useState('All')
  const [assessmentStatus, setAssessmentStatus] = useState('all')
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('all');
  // 'all' | UNASSIGNED | an assignee name as stored on the call's assignedto field.
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  // Guards against racing loadData calls (e.g. campaign switch + template reset):
  // only the most recent request may commit state.
  const loadSeq = useRef(0);

  const loadData = async (trig) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const dateParams = {};
    if (dateFrom) dateParams.dateFrom = String(new Date(`${dateFrom}T00:00:00`).getTime());
    if (dateTo) dateParams.dateTo = String(new Date(`${dateTo}T23:59:59.999`).getTime());
    try {
      if (selectedCampaign == "All"){
        if (startKey != '' && tsmpFrom != '' && trig) {
          const params = {
            count: window.pcaSettings?.api?.pageSize || 25,
            startKey: startKey,
            timestampFrom: tsmpFrom
          };

          // Add template filter alongside campaign
          if (selectedTemps !== 'All') {
            params.template = selectedTemps;
          }
          Object.assign(params, dateParams);
          const data = await list(params);
          if (seq !== loadSeq.current) return;
          const items = data.Records || data.Items || data;
          setStartKey(data.StartKey)
          setTsmpFrom(data.timestampFrom)
          const processedItems = items.map(withComputedRating);
          setCalls(prev => [...prev, ...processedItems]);
        } else {
          const params = {
            count: window.pcaSettings?.api?.pageSize || 25
          };

          // Add template filter alongside campaign
          if (selectedTemps !== 'All') {
            params.template = selectedTemps;
          }
          Object.assign(params, dateParams);
          const data = await list(params);
          if (seq !== loadSeq.current) return;
          const items = data.Records || data.Items || data;
          setStartKey(data.StartKey)
          setTsmpFrom(data.timestampFrom)
          const processedItems = items.map(withComputedRating);
          setCalls(processedItems);                    
        }
      }
      for (let i = 0; i <= campaigns.length -1; i++) {
       if (selectedCampaign == campaigns[i] && selectedCampaign != 'All') {
        if (campStartKey != '' && campAssigned != '' && trig) {
          const params = {
            count: window.pcaSettings?.api?.pageSize || 25,
            campaign: campaigns[i],
            startKey: campStartKey,
            campaignAssignedTo: campAssigned
          };

          // Add template filter alongside campaign
          if (selectedTemps !== 'All') {
            params.template = selectedTemps;
          }
          Object.assign(params, dateParams);
          const data = await list(params);
          if (seq !== loadSeq.current) return;
          setCampStartKey(data.StartKey)
          setcampAssigned(data.campaignAssignedTo)
          devLog("CAMP DATA ", data)
          const items = data.Records || data.Items || data;
          // setNextToken(data.NextToken)
          const processedItems = items.map(withComputedRating);
        setCalls(prev => [...prev, ...processedItems]);
        } else {
          const params = {
            count: window.pcaSettings?.api?.pageSize || 25,
            campaign: campaigns[i]
          };

          // Add template filter alongside campaign
          if (selectedTemps !== 'All') {
            params.template = selectedTemps;
          }
          Object.assign(params, dateParams);
          const data = await list(params);
          if (seq !== loadSeq.current) return;
          setCampStartKey(data.StartKey)
          setcampAssigned(data.campaignAssignedTo)
          devLog("CAMP DATA ", data)
          const items = data.Records || data.Items || data;
          // setNextToken(data.NextToken)
          const processedItems = items.map(withComputedRating);
          setCalls(processedItems);           
        }     
       }
      }
      

    } catch (err) {
      console.error('Failed to load calls', err);
      // 404 = no calls matched the filters, not a failure — show the empty state.
      if (err.status === 404) {
        if (seq === loadSeq.current) setCalls([]);
      } else {
        setAlert?.({
          variant: 'error',
          heading: 'Error',
          text: 'Failed to load calls.'
        });
      }
    } finally {
      if (seq === loadSeq.current) {
        setLoading(false);
      }
    }
  };const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      newDirection = 'desc';
    }
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const loadCampaigns = async () => {
    setCampaignLoading(true);
    try {
      const res = await listCampaignsApi();
      const tmps = (res.campaigns).map(c => [c.campaignName, c.templates]);
      const names = (res.campaigns || res).map(c => c.campaignName || c.name || c);
      setCampaigns(['All', ...names]);
      setTemps(['All', ...tmps])
    } catch (err) {
      console.error('Failed to load campaigns', err);
      setAlert?.({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load campaigns.'
      });
      const setVals = new Set(calls.map(c => c.customer));
      setCampaigns(['All', ...Array.from(setVals)]);
    } finally {
      setCampaignLoading(false);
    }
  };


  const handleAssign = (item, user) => {
    try {
      setAssignment(item.key || item.filename, user);
      setAssignmentsState(getAssignments());
    } catch (e) {
      console.error('Failed to assign agent', e);
      setAlert?.({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to assign agent.'
      });
    }
  };

  useEffect(() => {
    loadData();
    loadCampaigns();
    listAssignableUsers()
      .then((data) => setUsers(data.users || data.Users || data))
      .catch(err => console.error('Failed to load assignable users', err));
  }, [selectedCampaign, selectedTemps, dateFrom, dateTo]);

  useEffect(() => {
    setSelectedTemps('All');
    setSelectedJob('All');
  }, [selectedCampaign]);

  useEffect(() => {
    setSelectedJob('All');
  }, [selectedTemps]);

  // Load jobs based on loaded calls for the selected campaign
  useEffect(() => {
    if (calls.length > 0 && selectedCampaign !== 'All') {
      // Filter calls by selected campaign and optionally template
      const filteredCalls = calls.filter(c => {
        const matchesCampaign = c.customer === selectedCampaign;
        const matchesTemplate = selectedTemps === 'All' || c.template === selectedTemps;
        return matchesCampaign && matchesTemplate;
      });

      // Get unique job IDs from filtered calls
      const uniqueJobIds = [...new Set(
        filteredCalls
          .map(c => c.jobId)
          .filter(Boolean)
      )];
      setJobs(['All', ...uniqueJobIds]);
      if (selectedJob !== 'All' && !uniqueJobIds.includes(selectedJob)) {
        setSelectedJob('All');
      }
    } else {
      setJobs(['All']);
      setSelectedJob('All');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, selectedCampaign, selectedTemps]);

  useEffect(() => {
    if (!users.length || !calls.length) return;
    try {
      const token = window.localStorage.getItem('id_token');
      if (!token) return;
      const payload = payloadFromToken(token);
      const groups = payload['cognito:groups'];
      const username = payload['cognito:username'];
      const inGroup = Array.isArray(groups)
        ? groups.includes('QAAgent')
        : groups === 'QAAgent';
      if (!inGroup || !username) return;

      if (!users.find(u => (u.username || u.Username) === username)) {
        setUsers(prev => [...prev, { username }]);
      }

      calls.forEach(call => {
        const key = call.key || call.filename;
        if (!getAssignments()[key]) {
          setAssignment(key, username);
        }
      });
      setAssignedFiles([])
      setAssignmentsState(getAssignments());
    } catch (e) {
      console.error('Failed to set default assessor', e);
    }
  }, [users, calls]);

  useEffect(() => {
    const loadCheck = async () => {
      if (startKey == undefined && tsmpFrom == undefined){
        setAllLoad(false)
      } else {
        setAllLoad(true)
      }
      if (campAssigned == undefined && campStartKey == undefined){
        setCampLoad(false)
      } else {
        setCampLoad(true)
      }
    }
    loadCheck()
  }, [startKey, campAssigned, tsmpFrom, campStartKey])


  const agents = useMemo(() => {
    const filtered = selectedCampaign === 'All' ? calls : calls.filter(c => c.customer === selectedCampaign);
    const setVals = new Set(filtered.map(c => c.agent));
    return ['All', ...Array.from(setVals)];
  }, [calls, selectedCampaign]);

  const campaignCalls = useMemo(
    () =>
      selectedCampaign === 'All'
        ? calls
        : calls.filter(c => c.customer === selectedCampaign),
    [calls, selectedCampaign]
  );

  // Assignees present in the loaded calls for the selected campaign, so the
  // filter only offers names that will actually match rows.
  const assignees = useMemo(() => {
    const names = new Set(campaignCalls.map(c => c.assignedto).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [campaignCalls]);

  const campaignStats = useMemo(() => {
    const pass = campaignCalls.filter(c => c.computedRating === 'pass').length;
    const fail = campaignCalls.filter(c => c.computedRating === 'fail').length;
    const medium = campaignCalls.filter(c => c.computedRating === 'medium').length;
    const complianceFail = campaignCalls.filter(isComplianceFailure).length;
    const assessed = campaignCalls.filter(c => c.assessed === true).length;
    const outstanding = campaignCalls.length - assessed;
    return { pass, fail, medium, complianceFail, assessed, outstanding, total: campaignCalls.length };
  }, [campaignCalls]);
  
  const agentCalls = useMemo(() => {
    return campaignCalls.filter(c => {
      const r = (c.computedRating || '').toLowerCase();

      // Agent filter (OR logic for multiple agents)
      const matchesAgent = selectedAgents.length === 0 || selectedAgents.includes(c.agent);
      const matchesRating =
        selectedRating === 'all' ||
        (selectedRating === COMPLIANCE_FAIL ? isComplianceFailure(c) : r === selectedRating);
      const matchesTemplate = selectedTemps === 'All' || c.template === selectedTemps;
      const matchesJob = selectedJob === 'All' || c.jobId === selectedJob;

      // Assessment status filter
      const matchesAssessment =
        assessmentStatus === 'all' ||
        (assessmentStatus === 'assessed' && c.assessed === true) ||
        (assessmentStatus === 'not-assessed' && c.assessed !== true);

      // Compliance priority filter - existence-only, same semantics as the Search tab's
      // Priority filter. c.compliancePriorities is absent on calls indexed before this field
      // was added, so those calls simply never match a specific priority (no backfill).
      const matchesPriority =
        selectedPriority === 'all' ||
        (c.compliancePriorities || []).includes(selectedPriority);

      // Assigned To filter: the QA agent the call was handed to, or unassigned.
      const matchesAssignee =
        selectedAssignee === 'all' ||
        (selectedAssignee === UNASSIGNED ? !c.assignedto : c.assignedto === selectedAssignee);

      return matchesAgent && matchesRating && matchesTemplate && matchesJob && matchesAssessment && matchesPriority && matchesAssignee;
    });
  }, [campaignCalls, selectedAgents, selectedRating, selectedTemps, selectedJob, assessmentStatus, selectedPriority, selectedAssignee]);

  const searchedCalls = useMemo(() => {
    if (!searchQuery) return agentCalls;
    // if (selectedTemps != 'All') {

    // }
    const q = searchQuery.toLowerCase();
    return agentCalls.filter(c =>
      (c.agent || '').toLowerCase().includes(q) ||
      (c.customer || '').toLowerCase().includes(q) ||
      (c.jobName || c.filename || '').toLowerCase().includes(q) ||
      (c.key || '').toLowerCase().includes(q) ||
      (c.template || '').toLowerCase().includes(q)
    );
  }, [agentCalls, searchQuery]);

  const sortedCalls = useMemo(() => {
    let sortableItems = [...searchedCalls];

    if (sortField) {
      sortableItems.sort((a, b) => {
        let aVal, bVal;

        // Map the sortField to the actual data properties
        switch (sortField) {
          case 'computedRating':
            aVal = a.computedRating;
            bVal = b.computedRating;
            break;
          case 'callName':
            aVal = a.guid || a.jobId || '';
            bVal = b.guid || b.jobId || '';
            break;
          case 'compliance':
            aVal = a.Result?.Compliance ? 1 : 0;
            bVal = b.Result?.Compliance ? 1 : 0;
            break;
          case 'conduct':
            // Failures sort first; absent (nothing assessed) sorts with the passes.
            aVal = a.Result?.Conduct === false ? 0 : 1;
            bVal = b.Result?.Conduct === false ? 0 : 1;
            break;
          case 'qaScore': {
            // A call with no QA score (shown as "—") sorts last either way, not as a real 0.
            const noScore = sortDirection === 'asc' ? Number.MAX_SAFE_INTEGER : -Number.MAX_SAFE_INTEGER;
            aVal = qaScoreOf(a) ?? noScore;
            bVal = qaScoreOf(b) ?? noScore;
            break;
          }
          case 'assignedto':
            aVal = a.assignedto || 'Unassigned';
            bVal = b.assignedto || 'Unassigned';
            break;
          default:
            aVal = a[sortField];
            bVal = b[sortField];
        }

        // Handle timestamps explicitly
        if (sortField === 'timestamp') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        }

        let primaryComparison = 0;

        // Compare numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          primaryComparison = sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          // Compare strings/booleans
          if (typeof aVal === 'boolean') aVal = aVal ? 'true' : 'false';
          if (typeof bVal === 'boolean') bVal = bVal ? 'true' : 'false';

          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();

          primaryComparison = sortDirection === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
        }

        // Secondary sort by timestamp if values are identical
        if (primaryComparison === 0 && sortField !== 'timestamp') {
          const aTime = new Date(a.timestamp || 0).getTime();
          const bTime = new Date(b.timestamp || 0).getTime();
          return bTime - aTime;
        }

        return primaryComparison;
      });
    }

    return sortableItems;
  }, [searchedCalls, sortField, sortDirection]);

  const paginatedCalls = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedCalls.slice(start, start + pageSize);
  }, [sortedCalls, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchedCalls, pageSize]);

  const allFilteredIds = useMemo(
    () => searchedCalls.map(c => c.key || c.filename),
    [searchedCalls]
  );
  const allSelected =
    allFilteredIds.length > 0 &&
    allFilteredIds.every(id => assignedFiles.includes(id));

  // The signed-in user, from the same id_token claims App.js uses for the
  // top bar, so "Assign to me" lands on the name the Assigned-to-me page
  // filters by. The assignable-users list is preferred for the email since
  // that is what the assign Lambda keys agents on; the token claim is the
  // fallback for users outside that list.
  const currentUser = useMemo(() => {
    try {
      const token = window.localStorage.getItem('id_token');
      if (!token) return null;
      const payload = payloadFromToken(token) || {};
      const username = payload['cognito:username'];
      if (!username) return null;
      const listed = users.find(u => (u.username || u.Username) === username);
      const email = listed?.email || payload.email;
      return email ? { username, email } : null;
    } catch (e) {
      console.error('Failed to read signed-in user', e);
      return null;
    }
  }, [users]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        assignedFiles.length > 0 && !allSelected;
    }
  }, [assignedFiles, allSelected]);

  const stats = useMemo(() => {
     const pass = searchedCalls.filter(c => (c.computedRating || '').toLowerCase() === 'pass').length;
    const fail = searchedCalls.filter(c => (c.computedRating || '').toLowerCase() === 'fail').length;
    const medium = searchedCalls.filter(c => (c.computedRating || '').toLowerCase() === 'medium').length;
    const assessed = pass + fail + medium;
    const complianceFail = searchedCalls.filter(isComplianceFailure).length;
    return { pass, fail, medium, complianceFail, assessed, total: searchedCalls.length };
  }, [searchedCalls]);

  const barData = useMemo(
    () => ({
      labels: ['Pass', 'Medium', 'Fail'],
      datasets: [
        {
          label: '# of Calls',
          data: [campaignStats.pass, campaignStats.medium, campaignStats.fail],
          backgroundColor: ['#14b8a6', '#f59e0b', '#ef4444'],
          borderRadius: 6,
          maxBarThickness: 48
        }
      ]
    }),
    [campaignStats]
  );

  const handleBarClick = (elems) => {
    if (elems.length > 0) {
      const idx = elems[0].index;
      const label = barData.labels[idx];
      setSelectedRating(label.toLowerCase());
    }
  };

  const totalCalls = calls.length;
  const totalCampaigns = campaigns.length - 1;

  if (loading || campaignLoading) {
    return <LoadingState variant="stats" label="Loading campaigns" />;
  }

  const handleCheckboxChange = (key) => {
    setAssignedFiles(prev => {
      if (prev.includes(key)) {
        return prev.filter(item => item !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setAssignedFiles([]);
    } else {
      setAssignedFiles(allFilteredIds);
    }
  };

const toggleUser = (user) => {
  setSelectedUsers((prev) => {
    const exists = prev.find((u) => u.email === user.email);
    if (exists) {
      return prev.filter((u) => u.email !== user.email);
    } else {
      return [...prev, user];
    }
  });
};


  const handleAssignSubmit = async () => {
    let body = {
      callIds: assignedFiles,
      agents: selectedUsers
    }
    try {
      let res = await assignusers(body)
      devLog(res)
      setModalVis(false)
      setAlert?.({ variant: 'success', heading: 'Assigned', text: `Assigned ${assignedFiles.length} call(s) to ${selectedUsers.length} agent(s).` });
    } catch (err) {
      console.error('Assignment failed', err);
      if (err.status === 404) {
        setModalVis(false)
        setAlert?.({ variant: 'warning', heading: 'No Calls to Assign', text: 'No completed calls were found to assign.' });
      } else {
        setAlert?.({ variant: 'error', heading: 'Assignment Failed', text: 'Failed to assign the selected calls. Please try again.' });
      }
    }
  }

  const handleAssignToMe = async () => {
    if (!currentUser || assignedFiles.length === 0) return;
    const count = assignedFiles.length;
    const me = { email: currentUser.email, name: currentUser.username };
    try {
      await assignusers({
        callIds: assignedFiles,
        agents: [me],
        specificAgent: me.email,
        assignedBy: me.email
      });
      setAssignedFiles([]);
      setAlert?.({ variant: 'success', heading: 'Assigned', text: `Assigned ${count} call(s) to you.` });
    } catch (err) {
      console.error('Assign to me failed', err);
      if (err.status === 404) {
        setAlert?.({ variant: 'warning', heading: 'No Calls to Assign', text: 'No completed calls were found to assign.' });
      } else {
        setAlert?.({ variant: 'error', heading: 'Assignment Failed', text: 'Failed to assign the selected calls to you. Please try again.' });
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Home</h1>
          <p className="text-gray-600 mt-1">
            Monitor campaign performance and manage quality assessments
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <RefreshButton onRefresh={() => loadData()} setAlert={setAlert} />

          <button
            onClick={() => navigate('/campaigns/new')}
            className="btn-outline"
          >
            <FaPlus />
            New Campaign
          </button>

          <button
            onClick={() => navigate('/templates')}
            className="btn-primary"
          >
            <FaClipboardList />
            QA Scorecard
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        <span className="font-semibold text-gray-700">{totalCampaigns}</span> campaigns loaded
        <span className="mx-2 text-gray-300">•</span>
        Total calls across all campaigns: <span className="font-semibold text-gray-700">{totalCalls}</span>
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card space-y-3">
          <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Select Campaign</h3>
          <SelectField
            data-testid="campaign-filter"
            value={selectedCampaign}
            onChange={e => setSelectedCampaign(e.target.value)}
            className="select-field"
          >
            {campaigns.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectField>

          <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Filter by Date</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <DateTimeInput
              mode="date"
              data-testid="date-from-filter"
              value={dateFromInput}
              onChange={e => setDateFromInput(e.target.value)}
              className="input-field min-w-0 flex-1"
              aria-label="From date"
            />
            <DateTimeInput
              mode="date"
              data-testid="date-to-filter"
              value={dateToInput}
              onChange={e => setDateToInput(e.target.value)}
              className="input-field min-w-0 flex-1"
              aria-label="To date"
            />
            <button
              data-testid="date-apply-filter"
              onClick={() => {
                setDateFrom(dateFromInput);
                setDateTo(dateToInput);
              }}
              disabled={dateFromInput === dateFrom && dateToInput === dateTo}
              className="btn-primary flex-shrink-0 !px-4 !py-2 !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaCheck />
              Apply
            </button>
          </div>
          <p className="text-xs text-gray-500">Pick dates then click Apply (filters by call timestamp)</p>

          <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Compliance Priority</h3>
          <SelectField
            data-testid="compliance-priority-filter"
            value={selectedPriority}
            onChange={e => setSelectedPriority(e.target.value)}
            className="select-field"
          >
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </SelectField>
          <p className="text-xs text-gray-500">
            Matches calls with at least one Compliance question at this priority. Only calls processed after this
            filter's release have priority data attached.
          </p>

          <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Assigned To</h3>
          <SelectField
            data-testid="assigned-to-filter"
            value={selectedAssignee}
            onChange={e => setSelectedAssignee(e.target.value)}
            className="select-field"
          >
            <option value="all">All Assignees</option>
            {currentUser && assignees.includes(currentUser.username) && (
              <option value={currentUser.username}>Me ({currentUser.username})</option>
            )}
            <option value={UNASSIGNED}>Unassigned</option>
            {assignees
              .filter(a => a !== currentUser?.username)
              .map(a => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </SelectField>
          <p className="text-xs text-gray-500">Filter by the QA agent the call is assigned to</p>

          {selectedCampaign != 'All' && (
            <>
            <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Select Template</h3>
            <SelectField
              data-testid="campaign-filter"
              value={selectedTemps}
              onChange={e => setSelectedTemps(e.target.value)}
              className="select-field"
            >
              <option value="All">ALL</option>
              {temps
                .find(entry => Array.isArray(entry) && entry[0] === selectedCampaign)?.[1]
                ?.map(template => (
                  <option key={template} value={template}>
                    {template}
                  </option>
                )) || []}
            </SelectField>

            <h3 className="block text-sm font-semibold text-gray-800 mb-1.5">Select Job (Upload Batch)</h3>
            <SelectField
              data-testid="job-filter"
              value={selectedJob}
              onChange={e => setSelectedJob(e.target.value)}
              className="select-field"
            >
              {jobs.map(j => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </SelectField>
            <p className="text-xs text-gray-500">Filter by upload batch</p>
            </>
          )}
        </div>

        <div className="card">
          <h3 className="text-sm font-bold text-gray-900 mb-0.5">Call Results Distribution</h3>
          <p className="text-xs text-gray-400 mb-3">Click on a bar to filter the table below</p>
          <Bar data={barData} options={{ onClick: (_e, els) => handleBarClick(els) }} />
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
        <StatsCard
          testId="campaign-result"
          title="Result"
          value={
            <span className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-50 text-green-700 text-sm font-bold tabular-nums">
                {campaignStats.pass} P
              </span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 text-sm font-bold tabular-nums">
                {campaignStats.medium} M
              </span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 text-sm font-bold tabular-nums">
                {campaignStats.fail} F
              </span>
            </span>
          }
          subtitle="Pass / Medium / Fail"
          icon={<FaCheckCircle />}
          color="blue"
        />

        <StatsCard
          testId="campaign-volume"
          title="Volume"
          value={`${campaignStats.assessed} / ${campaignStats.outstanding}`}
          subtitle="Assessed / Outstanding"
          icon={<FaPhone />}
          color="yellow"
        />

        <StatsCard
          testId="campaign-pass-rate"
          title="Pass Rate"
          value={`${
            campaignStats.total
              ? Math.round((campaignStats.pass / campaignStats.total) * 100)
              : 0
          }%`}
          subtitle={`${campaignStats.pass} passing calls`}
          icon={<FaCheckCircle />}
          color="green"
        />

        <StatsCard
          testId="campaign-total"
          title="Total Calls"
          value={campaignStats.total}
          subtitle="All calls"
          icon={<FaPhone />}
          color="blue"
        />

        <StatsCard
          testId="campaign-failed"
          title="Failed Calls"
          value={campaignStats.fail}
          subtitle="Total failed"
          icon={<FaTimesCircle />}
          color="red"
        />

        <StatsCard
          testId="campaign-compliance-failed"
          title="Compliance Failed"
          value={campaignStats.complianceFail}
          subtitle="Critical compliance failures"
          icon={<FaTimesCircle />}
          color="red"
          onClick={() => setSelectedRating(COMPLIANCE_FAIL)}
        />
      </div>

      {/* Row 3 */}
      <div className="card space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-bold text-gray-900">Agent Performance</h3>
          <div className="flex flex-wrap gap-3 mt-2 sm:mt-0 items-center">
            <div className="w-64 flex-shrink-0">
              <MultiSelect
                data-testid="agent-filter"
                options={agents.filter(a => a !== 'All').map(a => ({ value: a, label: a }))}
                value={selectedAgents}
                onChange={values => setSelectedAgents(values || [])}
                placeholder="Select agents..."
              />
            </div>
            <SelectField
              data-testid="rating-filter"
              value={selectedRating}
              onChange={e => setSelectedRating(e.target.value)}
              className="select-field !w-40 flex-shrink-0"
            >
              <option value="all">All Ratings</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="medium">Medium</option>
              <option value={COMPLIANCE_FAIL}>Compliance Failed</option>
            </SelectField>
            <SelectField
              data-testid="assessment-filter"
              value={assessmentStatus}
              onChange={e => setAssessmentStatus(e.target.value)}
              className="select-field !w-40 flex-shrink-0"
            >
              <option value="all">All Status</option>
              <option value="assessed">Assessed</option>
              <option value="not-assessed">Not Assessed</option>
            </SelectField>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            testId="agent-total"
            title="Total Calls"
            value={stats.total}
            subtitle="Filtered calls"
            icon={<FaPhone />}
            color="blue"
          />
          <StatsCard
            testId="agent-pass-rate"
            title="Pass Rate"
            value={`${
              stats.total ? Math.round((stats.pass / stats.total) * 100) : 0
            }%`}
            subtitle={`${stats.pass} passing calls`}
            icon={<FaCheckCircle />}
            color="green"
          />
          <StatsCard
            testId="agent-failed"
            title="Failed Calls"
            value={stats.fail}
            subtitle="Total failed"
            icon={<FaTimesCircle />}
            color="red"
          />
          <StatsCard
            testId="agent-compliance-failed"
            title="Compliance Failed"
            value={stats.complianceFail}
            subtitle="Critical compliance failures"
            icon={<FaTimesCircle />}
            color="red"
          />
        </div>
      </div>
      {modalVis && (
        createPortal(<div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Attach Files to Users</h2>
              <button
                onClick={() => setModalVis(false)}
                aria-label="Close"
                className="p-1.5 -mt-1 -mr-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-ring flex-shrink-0"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4 max-h-60 overflow-y-auto">
              {users.length === 0 && (
                <p className="py-6 text-center text-sm text-gray-400">No assignable users found</p>
              )}
              {users.map((user) => (
                <label key={user.username} className="flex items-start gap-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`assign-user-${user.username}`}
                    checked={selectedUsers.some((u) => u.email === user.email)}
                    onChange={() => toggleUser({email: user.email, name: user.username})}
                    className="mt-1 h-4 w-4 accent-[#7c3aed] border-gray-200 rounded-lg"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{user.username} <span className="font-normal text-gray-500">({user.email})</span></p>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => setModalVis(false)}
                className="btn-outline"
              >
                <FaTimes />
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                className="btn-primary"
                data-testid="assign-confirm-btn"
              >
                <FaUserPlus />
                Attach
              </button>
            </div>
          </div>
        </div>, document.body)
      )}

      {/* Active Filters */}
      {(selectedCampaign !== 'All' || selectedTemps !== 'All' || selectedJob !== 'All' || selectedAgents.length > 0 || selectedRating !== 'all' || assessmentStatus !== 'all' || selectedPriority !== 'all' || selectedAssignee !== 'all' || dateFrom || dateTo) && (
        <div className="card">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Active Filters:</span>

            {selectedCampaign !== 'All' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Campaign: {selectedCampaign}
                <button
                  onClick={() => setSelectedCampaign('All')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove campaign filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedTemps !== 'All' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Template: {selectedTemps}
                <button
                  onClick={() => setSelectedTemps('All')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove template filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedJob !== 'All' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Job: {selectedJob}
                <button
                  onClick={() => setSelectedJob('All')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove job filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedAgents.map(agent => (
              <span key={agent} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Agent: {agent}
                <button
                  onClick={() => setSelectedAgents(prev => prev.filter(a => a !== agent))}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label={`Remove agent ${agent} filter`}
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            ))}

            {selectedRating !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Rating: {selectedRating === COMPLIANCE_FAIL
                  ? 'Compliance Failed'
                  : selectedRating.charAt(0).toUpperCase() + selectedRating.slice(1)}
                <button
                  onClick={() => setSelectedRating('all')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove rating filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {assessmentStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Status: {assessmentStatus === 'assessed' ? 'Assessed' : 'Not Assessed'}
                <button
                  onClick={() => setAssessmentStatus('all')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove assessment status filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedPriority !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Priority: {selectedPriority.charAt(0).toUpperCase() + selectedPriority.slice(1)}
                <button
                  onClick={() => setSelectedPriority('all')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove compliance priority filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedAssignee !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                Assigned To: {selectedAssignee === UNASSIGNED ? 'Unassigned' : selectedAssignee}
                <button
                  onClick={() => setSelectedAssignee('all')}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove assigned to filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {dateFrom && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                From: {dateFrom}
                <button
                  onClick={() => { setDateFrom(''); setDateFromInput(''); }}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove date-from filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            {dateTo && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium">
                To: {dateTo}
                <button
                  onClick={() => { setDateTo(''); setDateToInput(''); }}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                  aria-label="Remove date-to filter"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            )}

            <button
              onClick={() => {
                setSelectedCampaign('All');
                setSelectedTemps('All');
                setSelectedJob('All');
                setSelectedAgents([]);
                setSelectedRating('all');
                setAssessmentStatus('all');
                setSelectedPriority('all');
                setSelectedAssignee('all');
                setDateFrom('');
                setDateTo('');
                setDateFromInput('');
                setDateToInput('');
              }}
              className="ml-2 text-sm font-semibold text-red-700 hover:text-red-800 hover:underline"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Only the table scrolls sideways — the toolbar and pagination stay
          put, and the scrollbar hugs the table instead of the card bottom. */}
      <div className="card" data-tour="calls-table">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            {assignedFiles.length > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                {assignedFiles.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* One control for both assignment paths: a shortcut to the
                signed-in user, or the modal to pick QA agents. */}
            <ActionDropdown
              data-testid="assign-menu-btn"
              label="Assign"
              icon={<FaUserPlus />}
              buttonClassName="btn-outline !py-1.5 !px-3 !text-sm whitespace-nowrap"
              disabled={assignedFiles.length === 0}
              title={
                assignedFiles.length === 0
                  ? 'Select calls to assign'
                  : `Assign ${assignedFiles.length} selected call(s)`
              }
              actions={[
                {
                  label: 'Assign to me',
                  testId: 'assign-to-me-btn',
                  onClick: handleAssignToMe,
                  disabled: !currentUser,
                  title: currentUser
                    ? `Assign to ${currentUser.username}`
                    : 'Sign in to assign calls to yourself',
                },
                {
                  label: 'Choose QA agent…',
                  testId: 'assign-selected-btn',
                  onClick: () => setModalVis(true),
                },
              ]}
            />
            <div className="relative w-full sm:w-64">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[13px] pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search calls…"
                className="input-field !pl-9"
                aria-label="Search calls"
              />
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">
              Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, searchedCalls.length)} of {searchedCalls.length} calls
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-4 py-2.5 text-left bg-[#fafafc]">
                {/* Compact "All" keeps the column checkbox-sized; the full
                    name lives in the tooltip and screen-reader label. */}
                <label
                  className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap select-none"
                  title="Select all calls"
                >
                  <input
                    type="checkbox"
                    ref={selectAllRef}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all calls"
                    className="h-4 w-4 accent-[#7c3aed] border-gray-200 rounded-lg cursor-pointer"
                  />
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    All
                  </span>
                </label>
              </th>
              {[
                { id: 'computedRating', label: 'Rating' },
                { id: 'callName', label: 'Call' },
                { id: 'timestamp', label: 'Timestamp' },
                { id: 'conversationTime', label: 'Conversation Date' },
                { id: 'agent', label: 'Agent' },
                { id: 'conversationId', label: 'Conversation ID' },
                { id: 'dnis', label: 'DNIS' },
                { id: 'customer', label: 'Campaign' },
                { id: 'compliance', label: 'Compliance' },
                { id: 'conduct', label: 'Conduct' },
                { id: 'qaScore', label: 'QA Score' },
                { id: 'duration', label: 'Duration' },
                { id: 'callerSentimentScore', label: 'Cust Sent' },
                { id: 'assessed', label: 'Assessed' },
                { id: 'assignedto', label: 'Assignee' }
              ].map((column) => (
                <th
                  key={column.id}
                  className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer bg-[#fafafc] hover:text-gray-800 transition-colors whitespace-nowrap"
                  onClick={() => handleSort(column.id)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortField === column.id && (
                      <span className="text-cloudza-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedCalls.map((call, idx) => (
              <tr key={idx} className="group hover:bg-cloudza-50/40 transition-colors">
                <td className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    value={call.key || call.filename}
                    data-testid={`select-${call.key || call.filename}`}
                    checked={assignedFiles.includes(call.key || call.filename)}
                    onChange={() => handleCheckboxChange(call.key || call.filename)}
                    aria-label={`Select call ${call.guid || call.key || ''}`}
                    className="h-4 w-4 accent-[#7c3aed] border-gray-200 rounded-lg cursor-pointer"
                  />
                </td>
                <td className="px-5 py-3">
                  <span className={`status-badge status-${call.computedRating}`}>
                    {call.computedRating.charAt(0).toUpperCase() + call.computedRating.slice(1)}
                  </span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {/* Link (not target="_blank") so a plain click navigates in
                      place; ctrl/middle-click still opens a new tab. */}
                  <Link
                    to={`/dashboard/${call.key || call.filename}`}
                    className="inline-flex items-center gap-1.5 text-cloudza-600 font-medium hover:text-cloudza-800 hover:underline"
                    title={call.guid || call.jobId}
                  >
                    <span className="font-mono text-xs">{String(call.guid || call.jobId || '').slice(0, 8)}…</span>
                    View
                  </Link>
                </td>
                <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-600">{Formatter.Timestamp(call.timestamp)}</td>
                <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-600">{Formatter.Timestamp(call.conversationTime) || ""}</td>
                <td className="px-5 py-3 whitespace-nowrap">{call.agent}</td>
                <td className="px-5 py-3 font-mono text-xs whitespace-nowrap text-gray-500">
                  {call.conversationId
                    ? <span title={call.conversationId}>{String(call.conversationId).slice(0, 8)}…</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-3 font-mono text-xs whitespace-nowrap text-gray-500">
                  {call.dnis ? String(call.dnis).replace(/^tel:/i, '') : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-3 whitespace-nowrap">{call.customer}</td>
                <td className="px-5 py-3">
                  <span className={`status-badge ${call.Result?.Compliance ? 'status-pass' : 'status-fail'}`}>
                    {call.Result?.Compliance ? 'Pass' : 'Fail'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  {call.Result?.Conduct === false ? (
                    <span className="status-badge status-fail">Fail</span>
                  ) : call.Result?.Conduct === true ? (
                    <span className="status-badge status-pass">Pass</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-5 py-3 tabular-nums font-semibold text-gray-800">
                  {isJudgedOnCompliance(call)
                    ? <span className="text-gray-300" title="No QA score - judged on compliance">—</span>
                    : call.Result?.QA_Score}
                </td>
                <td className="px-5 py-3 tabular-nums text-gray-600 whitespace-nowrap">{call.duration}</td>
                <td className="px-5 py-3 tabular-nums text-gray-600">
                  {typeof call.callerSentimentScore === 'number' ? call.callerSentimentScore.toFixed(2) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-3">
                  <span className={`status-badge ${call.assessed === true ? 'status-completed' : 'status-not-started'}`}>
                    {call.assessed === true ? 'Assessed' : 'Not Assessed'}
                  </span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {call.assignedto == null && <span className="text-xs text-gray-400 italic">Unassigned</span> }
                  {call.assignedto != null && call.assignedto}
                  {/* <SelectField
                    data-testid={`assign-${call.key}`}
                    value={assignmentsState[call.key] || ''}
                    onChange={e => handleAssign(call, e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1"
                  >
                    <option value="">Select</option>
                    {users.map(u => (
                      <option
                        key={u.username || u.Username}
                        value={u.username || u.Username}
                      >
                        {u.username || u.Username}
                      </option>
                    ))}
                  </SelectField> */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {searchedCalls.length === 0 && (
          <div className="py-10 px-6 text-center">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
              <FaPhone className="w-5 h-5" />
            </span>
            <p className="mt-4 text-sm font-semibold text-gray-800">No calls match the current filters</p>
            <p className="mt-1 text-sm text-gray-400">Try adjusting the search or clearing the active filters above</p>
          </div>
        )}
        <div className="px-4 py-3 flex flex-wrap items-center justify-center sm:justify-between gap-3 border-t border-gray-200 text-xs text-gray-500">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <span>
              Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, searchedCalls.length)} of {searchedCalls.length}
            </span>
            <SelectField
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="select-field w-24"
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </SelectField>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-outline !py-1 !px-3 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, Math.ceil(searchedCalls.length / pageSize)) }, (_, i) => {
                const total = Math.ceil(searchedCalls.length / pageSize);
                let pageNum;
                if (total <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= total - 2) { 
                  pageNum = total - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-[28px] h-7 px-2 rounded-lg text-xs font-semibold transition-colors ${currentPage === pageNum ? 'bg-cloudza-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(searchedCalls.length / pageSize), currentPage + 1))}
              disabled={currentPage === Math.ceil(searchedCalls.length / pageSize)}
              className="btn-outline !py-1 !px-3 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {(selectedCampaign == 'All' && allLoad) && (
        <button className="btn-outline !py-1.5 !px-3 !text-sm whitespace-nowrap" onClick={() => loadData(true)}>
          <FaChevronDown />
          Load More Calls
        </button>        
      )}
      {(selectedCampaign != 'All' && campLoad) && (
        <button className="btn-outline !py-1.5 !px-3 !text-sm whitespace-nowrap" onClick={() => loadData(true)}>
          <FaChevronDown />
          Load More {selectedCampaign} Calls
        </button>        
      )}

      <div className="text-[11px] text-gray-400">Campaign statistics loaded</div>
    </div>
  );
};

// Simple stats card component reused from CampaignHomePage
// Icon tile tints per metric type — all from the dark-remapped 50/700 ramps.
const STAT_TILE_TINTS = {
  blue: 'bg-cloudza-50 text-cloudza-700',
  green: 'bg-green-50 text-green-700',
  yellow: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
};

const StatsCard = ({ title, value, subtitle, icon, color = 'blue', testId, onClick }) => {
  // Clickable when given onClick - it then filters the table, like a bar in the chart does.
  const clickable = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
      }
    : {};
  return (
    <div className={`card-hover${onClick ? ' cursor-pointer' : ''}`} data-testid={testId} {...clickable}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {title}
          </p>
          <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1 truncate">{value}</div>
          {subtitle && <p className="text-xs text-gray-400 mt-1 truncate">{subtitle}</p>}
        </div>
        <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm ${STAT_TILE_TINTS[color] || STAT_TILE_TINTS.blue}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboard;